import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Inbox, Search, Eye, Users, AlertTriangle, X, MapPin, Phone, Mail, Clock, Hash, Image as ImageIcon, Trash2, MessageSquare } from 'lucide-react';
import { toast } from 'react-toastify';
import { getAllForumPosts, getForumPostInterests, deleteForumPost } from '../../services/controlPanelApi';
import Spinner from '../Spinner/Spinner';
import './PostsManagement.css';

// Mapeo de estado del post → etiqueta + clase de color (coincide con el enum PostStatus del backend)
const STATUS_META = {
    ACTIVE: { label: 'Activo', cls: 'active' },
    PAUSED: { label: 'Pausado', cls: 'paused' },
    RESOLVED: { label: 'Resuelto', cls: 'assigned' },
    CLOSED: { label: 'Cerrado', cls: 'cancelled' },
    EXPIRED: { label: 'Expirado', cls: 'expired' },
};

const statusMeta = (status) => STATUS_META[status] || { label: status || 'Desconocido', cls: 'unknown' };

// Los Timestamp de Google llegan como { seconds, nanos }; también soportamos string/number.
const parseTimestamp = (ts) => {
    if (!ts) return null;
    if (typeof ts === 'object' && ts.seconds !== undefined) return new Date(ts.seconds * 1000);
    if (typeof ts === 'string' || typeof ts === 'number') {
        const d = new Date(ts);
        return isNaN(d.getTime()) ? null : d;
    }
    return null;
};

const formatDate = (ts, withTime = false) => {
    const d = parseTimestamp(ts);
    if (!d) return '—';
    const opts = withTime
        ? { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }
        : { day: '2-digit', month: '2-digit', year: 'numeric' };
    return d.toLocaleString('es-AR', opts);
};

const isUrgent = (post) => post.urgent === true || post.isUrgent === true;

const PostsManagement = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('TODOS');
    const [search, setSearch] = useState('');
    const [selectedPost, setSelectedPost] = useState(null);

    const fetchPosts = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getAllForumPosts();
            setPosts(Array.isArray(data) ? data : (data?.posts || []));
        } catch (err) {
            console.error('Error cargando posts:', err);
            toast.error('No se pudieron cargar los posts.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPosts();
    }, [fetchPosts]);

    // Conteo por estado para los chips
    const counts = useMemo(() => {
        const c = { TODOS: posts.length };
        posts.forEach((p) => { c[p.status] = (c[p.status] || 0) + 1; });
        return c;
    }, [posts]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return posts.filter((p) => {
            if (statusFilter !== 'TODOS' && p.status !== statusFilter) return false;
            if (!q) return true;
            return (
                (p.title || '').toLowerCase().includes(q) ||
                (p.description || '').toLowerCase().includes(q) ||
                (p.authorName || '').toLowerCase().includes(q) ||
                (p.authorEmail || p.authorId || '').toLowerCase().includes(q)
            );
        });
    }, [posts, statusFilter, search]);

    const filterChips = ['TODOS', 'ACTIVE', 'PAUSED', 'RESOLVED', 'CLOSED', 'EXPIRED'];

    const handleDelete = useCallback(async (post) => {
        if (!window.confirm(`¿Eliminar definitivamente el post "${post.title || '(sin título)'}"? Esta acción no se puede deshacer.`)) {
            return;
        }
        try {
            await deleteForumPost(post.id);
            toast.success('Post eliminado');
            setPosts((prev) => prev.filter((p) => p.id !== post.id));
            setSelectedPost((cur) => (cur && cur.id === post.id ? null : cur));
        } catch (err) {
            console.error('Error eliminando post:', err);
            toast.error('No se pudo eliminar el post.');
        }
    }, []);

    return (
        <div className="cme-posts">
            {loading && <Spinner />}

            <header className="cme-posts__header">
                <div>
                    <h1 className="cme-posts__title">Posts del foro</h1>
                    <p className="cme-posts__subtitle">
                        {posts.length} {posts.length === 1 ? 'post creado' : 'posts creados'} en total
                    </p>
                </div>
                <div className="cme-posts__search">
                    <Search size={16} />
                    <input
                        type="text"
                        placeholder="Buscar por título, autor o descripción…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </header>

            <div className="cme-posts__filters" role="tablist" aria-label="Filtrar por estado">
                {filterChips.map((key) => {
                    const label = key === 'TODOS' ? 'Todos' : statusMeta(key).label;
                    const n = counts[key] || 0;
                    return (
                        <button
                            key={key}
                            type="button"
                            className={`cme-posts__chip ${statusFilter === key ? 'is-active' : ''}`}
                            onClick={() => setStatusFilter(key)}
                            aria-pressed={statusFilter === key}
                        >
                            {label} <span className="cme-posts__chip-count">{n}</span>
                        </button>
                    );
                })}
            </div>

            {filtered.length > 0 ? (
                <div className="cme-posts__table-wrap">
                    <table className="cme-posts__table">
                        <thead>
                            <tr>
                                <th>Estado</th>
                                <th>Título</th>
                                <th>Autor</th>
                                <th>Categorías</th>
                                <th>Zona</th>
                                <th className="cme-posts__num">Interesados</th>
                                <th className="cme-posts__num">Vistas</th>
                                <th>Fecha</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((post) => {
                                const meta = statusMeta(post.status);
                                return (
                                    <tr
                                        key={post.id}
                                        className="cme-posts__row"
                                        onClick={() => setSelectedPost(post)}
                                        title="Ver detalle"
                                    >
                                        <td>
                                            <span className={`cme-posts__status cme-posts__status--${meta.cls}`}>
                                                {meta.label}
                                            </span>
                                        </td>
                                        <td className="cme-posts__title-cell">
                                            {isUrgent(post) && (
                                                <AlertTriangle size={14} className="cme-posts__urgent" title="Urgente" />
                                            )}
                                            <span>{post.title || '(sin título)'}</span>
                                        </td>
                                        <td>
                                            <div className="cme-posts__author">
                                                <span className="cme-posts__author-name">{post.authorName || '—'}</span>
                                                <span className="cme-posts__author-email">{post.authorEmail || post.authorId || ''}</span>
                                            </div>
                                        </td>
                                        <td>{(post.categories && post.categories.join(', ')) || '—'}</td>
                                        <td>{post.serviceLocationId || '—'}</td>
                                        <td className="cme-posts__num">{post.totalInterested ?? 0}</td>
                                        <td className="cme-posts__num">{post.views ?? 0}</td>
                                        <td className="cme-posts__date">{formatDate(post.createdAt)}</td>
                                        <td>
                                            <button
                                                type="button"
                                                className="cme-posts__delete-btn"
                                                onClick={(e) => { e.stopPropagation(); handleDelete(post); }}
                                                title="Eliminar post"
                                                aria-label="Eliminar post"
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                !loading && (
                    <div className="cme-posts__empty">
                        <div className="cme-posts__empty-icon"><Inbox size={42} /></div>
                        <p className="cme-posts__empty-text">No hay posts para mostrar.</p>
                        <p className="cme-posts__empty-hint">
                            {search || statusFilter !== 'TODOS'
                                ? 'Probá ajustar el filtro o la búsqueda.'
                                : 'Todavía no se creó ningún post.'}
                        </p>
                    </div>
                )
            )}

            {selectedPost && (
                <PostDetailModal
                    post={selectedPost}
                    onClose={() => setSelectedPost(null)}
                    onDelete={handleDelete}
                />
            )}
        </div>
    );
};

// Modal de detalle completo de un post
const PostDetailModal = ({ post, onClose, onDelete }) => {
    const meta = statusMeta(post.status);
    const images = post.images || [];

    const [interests, setInterests] = useState([]);
    const [loadingInterests, setLoadingInterests] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoadingInterests(true);
        getForumPostInterests(post.id)
            .then((data) => {
                if (cancelled) return;
                // El backend puede devolver una lista o {interests: [...]}
                setInterests(Array.isArray(data) ? data : (data?.interests || []));
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('Error cargando interesados:', err);
                setInterests([]);
            })
            .finally(() => { if (!cancelled) setLoadingInterests(false); });
        return () => { cancelled = true; };
    }, [post.id]);

    return (
        <div className="cme-posts__modal-backdrop" onClick={onClose}>
            <div className="cme-posts__modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <button type="button" className="cme-posts__modal-close" onClick={onClose} aria-label="Cerrar">
                    <X size={18} />
                </button>

                <div className="cme-posts__modal-head">
                    <span className={`cme-posts__status cme-posts__status--${meta.cls}`}>{meta.label}</span>
                    {isUrgent(post) && (
                        <span className="cme-posts__urgent-badge"><AlertTriangle size={13} /> Urgente</span>
                    )}
                </div>

                <h2 className="cme-posts__modal-title">{post.title || '(sin título)'}</h2>
                <p className="cme-posts__modal-desc">{post.description || '(sin descripción)'}</p>

                <div className="cme-posts__modal-grid">
                    <Detail icon={<Users size={15} />} label="Autor" value={post.authorName} />
                    <Detail icon={<Mail size={15} />} label="Email" value={post.authorEmail || post.authorId} />
                    <Detail icon={<Phone size={15} />} label="Teléfono" value={post.authorPhone || '—'} />
                    <Detail icon={<MapPin size={15} />} label="Zona" value={post.serviceLocationId || '—'} />
                    <Detail icon={<Hash size={15} />} label="Categorías" value={(post.categories && post.categories.join(', ')) || '—'} />
                    <Detail icon={<Clock size={15} />} label="Horario preferido" value={post.preferredVisitTime || '—'} />
                    <Detail icon={<Users size={15} />} label="Interesados" value={post.totalInterested ?? 0} />
                    <Detail icon={<Eye size={15} />} label="Vistas" value={post.views ?? 0} />
                    <Detail icon={<ImageIcon size={15} />} label="Imágenes" value={images.length} />
                    <Detail icon={<Clock size={15} />} label="Creado" value={formatDate(post.createdAt, true)} />
                    <Detail icon={<Clock size={15} />} label="Actualizado" value={formatDate(post.updatedAt, true)} />
                    <Detail label="Consintió compartir teléfono" value={post.phoneConsent ? 'Sí' : 'No'} />
                    <Detail label="ID" value={post.id} mono />
                </div>

                <div className="cme-posts__interests">
                    <h3 className="cme-posts__interests-title">
                        <Users size={15} />
                        Proveedores interesados
                        <span className="cme-posts__interests-count">{interests.length}</span>
                    </h3>

                    {loadingInterests ? (
                        <p className="cme-posts__interests-empty">Cargando interesados…</p>
                    ) : interests.length === 0 ? (
                        <p className="cme-posts__interests-empty">Todavía ningún proveedor mostró interés.</p>
                    ) : (
                        <ul className="cme-posts__interests-list">
                            {interests.map((it) => (
                                <li key={it.id || it.providerId} className="cme-posts__interest">
                                    <div className="cme-posts__interest-head">
                                        <span className="cme-posts__interest-name">{it.providerName || it.providerId}</span>
                                        {it.providerType && (
                                            <span className="cme-posts__interest-type">{it.providerType}</span>
                                        )}
                                        <span className="cme-posts__interest-date">{formatDate(it.createdAt)}</span>
                                    </div>
                                    <div className="cme-posts__interest-contact">
                                        {it.providerPhone && (<span><Phone size={12} /> {it.providerPhone}</span>)}
                                        {it.providerEmail && (<span><Mail size={12} /> {it.providerEmail}</span>)}
                                    </div>
                                    {it.message && (
                                        <div className="cme-posts__interest-msg">
                                            <MessageSquare size={12} /> {it.message}
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="cme-posts__modal-actions">
                    <button
                        type="button"
                        className="cme-posts__delete-btn cme-posts__delete-btn--wide"
                        onClick={() => onDelete(post)}
                    >
                        <Trash2 size={15} /> Eliminar post
                    </button>
                </div>
            </div>
        </div>
    );
};

const Detail = ({ icon, label, value, mono }) => (
    <div className="cme-posts__detail">
        <span className="cme-posts__detail-label">{icon}{label}</span>
        <span className={`cme-posts__detail-value ${mono ? 'is-mono' : ''}`}>{value ?? '—'}</span>
    </div>
);

export default PostsManagement;
